import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  CollaboratorsList,
  DealCollaboratorAddInput,
  PartnerCollaboratorEntry,
  VendorCollaboratorEntry,
} from "./types";

// POST /api/admin/deals/:hubspotDealId/collaborators
//
// Replaces /app/admin/deals/_actions.ts::addDealCollaborator. Optimistic
// list-append (Bucket 3 deal-comment pattern) on the
// queryKeys.deals.collaborators(hubspotDealId) cache. The temp row carries
// `id: temp-<uuid>` + `pending: true` so Phase 7's UI can render it
// distinctly until the server response lands and onSettled refetches the
// real row (with name/avatar/email).
//
// 409 from the worker means the unique violation (deal_collaborators
// already has this (side, user/partner_user_id) pair) — surfaced as the
// legacy "Already a collaborator on this deal" string.

type Vars = { hubspotDealId: string } & DealCollaboratorAddInput;

type Context = {
  previous: CollaboratorsList | undefined;
  key: ReturnType<typeof queryKeys.deals.collaborators>;
};

export function useAddDealCollaborator() {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiClientError, Vars, Context>({
    mutationFn: ({ hubspotDealId, ...body }) =>
      apiFetch<{ id: string }>(
        `/admin/deals/${hubspotDealId}/collaborators`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      ),
    onMutate: async (vars) => {
      const key = queryKeys.deals.collaborators(vars.hubspotDealId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CollaboratorsList>(key);
      const tempId = `temp-${crypto.randomUUID()}`;
      queryClient.setQueryData<CollaboratorsList>(key, (old) => {
        const base: CollaboratorsList = old ?? { vendor: [], partner: [] };
        if (vars.side === "vendor") {
          const optimistic: VendorCollaboratorEntry = {
            side: "vendor",
            id: tempId,
            user_id: vars.userId,
            name: null,
            email: null,
            avatar_url: null,
            pending: true,
          };
          return { ...base, vendor: [...base.vendor, optimistic] };
        }
        const optimistic: PartnerCollaboratorEntry = {
          side: "partner",
          id: tempId,
          partner_user_id: vars.partnerUserId,
          name: null,
          email: null,
          pending: true,
        };
        return { ...base, partner: [...base.partner, optimistic] };
      });
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(err.message ?? "Failed to add collaborator");
    },
    onSettled: (_data, _err, { hubspotDealId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.deals.collaborators(hubspotDealId),
      });
      // Phase 7 sub-bucket 16 — admin drawer payload embeds the
      // collaborators array directly, so sweep `admin.deals.detail(id)`
      // too even though Pattern A optimism already patched the
      // collaborators sub-cache.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.admin.deals.detail(hubspotDealId),
      });
    },
    onSuccess: () => {
      toast.success("Collaborator added");
    },
  });
}
